import { Button, mergeClasses } from '@fluentui/react-components';
import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Flex, StyledText } from '@tdp/common/fluentV9MigrationComponents';
import { useDocumentTitle } from '@tdp/common/shared';
import { themeSlice } from '@tdp/common/theme';
import { useSelector } from '@tdp/core';

import { IContentFeedCard } from './Card';
import { HomeLinks, HomePageAssets, ToolIcons, ToolLinks } from './constants';
import { ContentFeedLayout } from './ContentFeedLayout';
import { CopilotCTA } from './CopilotCTA';
import { useStyle } from './HomePage.Styles';
import { IToolTile } from './ToolTile';
import { ToolTileLayout } from './ToolTileLayout';
import { AdaptiveCardDialogNew } from '../adaptiveCards/pages/AdaptiveCardDialogNew';
import { AppListImportDialog } from '../apps/appList/components/AppListImportDialog';
import { AppListNewDialog } from '../apps/appList/components/AppListNewDialog';

export const Ids = {
  homePageTitle: 'home-page-title',
  homePageSubtitle: 'home-page-subtitle',
};

export const Home: React.FC = () => {
  const intl = useIntl();
  const [newDialog, setNewDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [showCard, setShowAdaptiveCardCta] = useState(false);
  const [showCopilotCta, setShowCopilotCta] = useState(false);
  const [seeMoreEnabled, setSeeMoreEnabled] = useState(false);
  const [finalTiles, setFinalTiles] = useState<IToolTile[]>([]);

  const newHomePageClasses = useStyle();
  const currentTheme = useSelector(themeSlice.selectors.getCurrentTheme);
  useDocumentTitle(intl.formatMessage({ defaultMessage: "Home", description: "Home page title" }));
  // TODO: Add correct list of cards.
  const cards: IContentFeedCard[] = [
    {
      title: intl.formatMessage({
        defaultMessage: 'Create a new app',
        description: 'App create tile on the home page feed',
      }),
      content: intl.formatMessage({
        defaultMessage: 'Create and register a new app in minutes.',
        description: 'App create tile on the home page feed description',
      }),
      image:
        currentTheme === 'light'
          ? HomePageAssets.createLight
          : HomePageAssets.createDark,
      onClick: () => {
        setNewDialog(true);
      },
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Import an app',
        description: 'App import tile on the home page feed',
      }),
      content: intl.formatMessage({
        defaultMessage: 'Create and register a new app in minutes.',
        description: 'App create tile on the home page feed description',
      }),
      image: HomePageAssets.importLight,
      onClick: () => {
        setImportDialog(true);
      },
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'Design an adaptive card',
        description: 'Adaptive card create tile on the home page feed',
      }),
      content: intl.formatMessage({
        defaultMessage:
          'Create an adaptive card for a better user experience in Teams, Outlook, and Copilot.',
        description:
          'Adaptive card create tile on the home page feed description',
      }),
      onClick: () => {
        setShowAdaptiveCardCta(true);
      },
      image:
        currentTheme === 'light'
          ? HomePageAssets.cardLight
          : HomePageAssets.cardDark,
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'New to Copilot extensibility?',
        description: 'Copilot introduction card tile on the home page feed',
      }),
      content: intl.formatMessage({
        defaultMessage:
          'Understand your options for extending Copilot and creating custom AI agents.',
        description:
          'Copilot introduction card tile on the home page feed description',
      }),
      onClick: () => {
        setShowCopilotCta(true);
      },
      image: HomePageAssets.copilot,
    },
  ];

  const feed: ReactNode = (
    <Flex fill className={newHomePageClasses.feed}>
      <ContentFeedLayout cards={cards} />
    </Flex>
  );

  // "Tools for configuring, validating, and distributing apps to an organization or the app store.",
  const hero: ReactNode = (
    <Flex
      column
      fill
      className={mergeClasses(
        newHomePageClasses.heroImage,
        currentTheme === 'dark' && newHomePageClasses.heroImageDark
      )}
    >
      <Flex column className={newHomePageClasses.heroText}>
        <StyledText
          id={Ids.homePageTitle}
          as="h1"
          className={newHomePageClasses.heroTitle}
          size="larger"
          weight="semibold"
        >
          {intl.formatMessage({
            defaultMessage:
              'Manage the apps and agents you build for Copilot and Microsoft 365 apps',
            description: 'The title that appears on the home page',
          })}
        </StyledText>
        <StyledText
          className={newHomePageClasses.heroSubtitle}
          size="small"
          id={Ids.homePageSubtitle}
        >
          {intl.formatMessage({
            defaultMessage:
              'Find tools to configure, validate, and publish your apps and agents',
            description:
              "The subtitle/description that falls underneath the home page banner/hero's opening text",
          })}
        </StyledText>
      </Flex>
      {feed}
    </Flex>
  );

  const tiles: IToolTile[] = [
    {
      id: 'HomeM365PlatformDocsButton',
      icon: ToolIcons.m365DevDoc,
      link: HomeLinks.M365DevDocs,
      title: intl.formatMessage({
        defaultMessage: 'Microsoft 365 developer documentation',
        description:
          'Home page Tool Tile Microsoft 365 developer Documentation link title',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'Find documentation, sample code, tutorials, and more to help you integrate your app into Microsoft 365.',
        description:
          'Home page Tool Tile Microsoft 365 Documentation link description',
      }),
    },
    {
      id: 'HomeUIToolkitButton',
      icon: ToolIcons.m365UIKit,
      link: ToolLinks.M365KitFigma,
      title: intl.formatMessage({
        defaultMessage: 'Microsoft 365 UI Kit (Preview) ',
        description: 'Home page Tool Tile Microsoft 365 UI Kit link title',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'Design apps that span the Microsoft 365 ecosystem, using components, samples, and elements that you can drag, drop, and modify.',
        description:
          'Home page Tool Tile Microsoft Teams UI Kit link description',
      }),
    },
    {
      id: 'HomeAppComplianceButton',
      icon: ToolIcons.appCompliance,
      link: HomeLinks.TeamsCertificationDocs,
      title: intl.formatMessage({
        defaultMessage: 'Microsoft 365 App Compliance Program',
        description: "Home page Tool Tile 'App Certification' slide title",
      }),
      description: intl.formatMessage({
        defaultMessage:
          'Build trust with Microsoft 365 admins by proving your app meets security, privacy, and data-handling standards.',
        description:
          "Home page Tool Tile 'App Certification' slide subtitle/description",
      }),
    },
    {
      id: 'HomeDeveloperBlogButton',
      icon: ToolIcons.developerBlog,
      link: HomeLinks.M365DeveloperBlog,
      title: intl.formatMessage({
        defaultMessage: 'Microsoft 365 developer blog',
        description: 'Home page Tool Tile Microsoft 365 Developer Blog title',
      }),
      description: intl.formatMessage({
        defaultMessage: 'Get the latest news for Microsoft 365 developers.',
        description:
          'Home page Tool Tile Microsoft 365 Developer Blog description',
      }),
    },
    {
      id: 'HomeSampleAppsRepositoryButton',
      icon: ToolIcons.sampleAppsRepository,
      link: HomeLinks.SampleAppsRepository,
      title: intl.formatMessage({
        defaultMessage: 'Teams sample repository',
        description: 'Home page Tool Tile Teams Sample Apps repository title',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'Save time with samples that demonstrate basic app development concepts and code structures.',
        description: 'Home page Tool Tile Sample Apps repository description',
      }),
    },
    {
      id: 'HomeVSCodeButton',
      icon: ToolIcons.vscode,
      link: ToolLinks.VSCodeExt,
      title: intl.formatMessage({
        defaultMessage: 'Microsoft 365 Agents Toolkit for Visual Studio Code',
        description:
          'Home page Tool Tile Visual Studio Code Extension link title',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'This Visual Studio Code extension is the fastest way to build, test, and deploy a Teams app.',
        description:
          'Home page Tool Tile Visual Studio Code Extension link description',
      }),
    },
  ];

  const learnMoreTiles: IToolTile[] = [
    {
      id: 'TeamsUIKItButton',
      icon: ToolIcons.teamsUIKit,
      link: ToolLinks.TeamsUITemplatesDocs,
      title: intl.formatMessage({
        defaultMessage: 'Microsoft Teams UI Kit',
        description: 'Home page Tool Tile Microsoft Teams UI Kit link title',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'Design your app with components and templates you can drag, drop, and modify.',
        description:
          'Home page Tool Tile Microsoft Teams UI Kit link description',
      }),
    },
    {
      id: 'HomeTeamsPlatformDocsButton',
      icon: ToolIcons.teams,
      link: ToolLinks.TeamsPlatformDocs,
      title: intl.formatMessage({
        defaultMessage: 'Teams platform documentation',
        description:
          'Home page Tool Tile Microsoft Teams Platform Documentation link title',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'Get documentation, sample code, tutorials, and more to help you build custom Microsoft Teams apps.',
        description:
          'Home page Tool Tile Microsoft Teams Platform Documentation link description',
      }),
    },
  ];

  const onSeeMoreClick = () => {
    setSeeMoreEnabled(!seeMoreEnabled);
  };

  useEffect(() => {
    if (seeMoreEnabled) {
      setFinalTiles([
        ...tiles.slice(0, 4),
        ...learnMoreTiles,
        ...tiles.slice(4, 7),
      ]);
    } else {
      setFinalTiles([...tiles]);
    }
  }, [seeMoreEnabled]);

  const resources: ReactNode = (
    <Flex column className={newHomePageClasses.resources}>
      <Flex style={{ width: '100%', justifyContent: 'space-between' }}>
        <StyledText
          className={newHomePageClasses.resourceTitle}
          weight="bold"
          size="large"
          as="h2"
        >
          {intl.formatMessage({
            defaultMessage: 'Resources',
            description: 'Home page Resources section title',
          })}
        </StyledText>
        <Button
          appearance="transparent"
          onClick={onSeeMoreClick}
          data-testid="see-more-button"
        >
          {seeMoreEnabled
            ? intl.formatMessage({
              defaultMessage: 'See less',
              description: 'see less button',
            })
            : intl.formatMessage({
              defaultMessage: 'See more',
              description: 'see more button',
            })}
        </Button>
      </Flex>
      <ToolTileLayout tiles={finalTiles} />
    </Flex>
  );

  const Body: ReactNode = (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
      }}
    >
      {newDialog && <AppListNewDialog
        open={newDialog}
        setOpen={setNewDialog}
      />}
      {importDialog && <AppListImportDialog
        open={importDialog}
        setOpen={setImportDialog}
      />}
      {showCard && <AdaptiveCardDialogNew
        open={showCard}
        setOpen={setShowAdaptiveCardCta}
      />}
      <CopilotCTA show={showCopilotCta} setShow={setShowCopilotCta} />
      <Flex column className={newHomePageClasses.container} id="main">
        <Flex column className={newHomePageClasses.inner}>
          {hero}
          {resources}
        </Flex>
      </Flex>
    </div>
  );

  return Body;
};
